import { useGameStoreApi } from "#/game/GameStoreProvider";
import { useGameHotkeys } from "#/game/hooks/useGameHotkeys";
import type { GameStoreApi } from "#/game/store";
import {
  canEndTurnFromState,
  canUndoCommittedTurnFromState,
  canUndoPendingMovesFromState,
} from "#/game/store/selectors";

function confirmPlayMapSelection(api: GameStoreApi): void {
  const s = api.getState();
  const { selection } = s;
  if (selection.mode === "confirm-attack") {
    s.confirmAttack();
    return;
  }
  if (selection.mode === "confirm-capture-reset") {
    s.confirmCaptureReset();
    return;
  }
  if (selection.mode === "move-preview") {
    s.selectTile(selection.destination);
    return;
  }
  if (selection.mode === "unit") {
    const unit = s.localState.units.find((u) => u.id === selection.unitId);
    if (unit) s.selectTile(unit.pos);
  }
}

function canUndoForHotkeys({
  api,
  allowUndoFullTurn,
}: {
  api: GameStoreApi;
  allowUndoFullTurn: boolean;
}): boolean {
  const state = api.getState();
  if (canUndoPendingMovesFromState(state)) return true;
  return allowUndoFullTurn && canUndoCommittedTurnFromState(state);
}

export function usePlayMapGameHotkeys({
  onEndTurn,
  onUndo,
  paused,
  allowUndoFullTurn = true,
}: {
  onEndTurn: () => void | Promise<void>;
  onUndo: () => void;
  paused?: boolean;
  allowUndoFullTurn?: boolean;
}) {
  const api = useGameStoreApi();
  useGameHotkeys({
    canInteract: () => {
      if (paused) return false;
      const s = api.getState();
      return (
        s.localState.status === "in_progress" && !s.isSubmitting && !s.animation && !s.aiPlayback
      );
    },
    canConfirmSelection: () => {
      const mode = api.getState().selection.mode;
      return (
        mode === "confirm-attack" ||
        mode === "confirm-capture-reset" ||
        mode === "move-preview" ||
        mode === "unit"
      );
    },
    onConfirmSelection: () => confirmPlayMapSelection(api),
    canUndo: () => canUndoForHotkeys({ api, allowUndoFullTurn }),
    canEndTurn: () => canEndTurnFromState(api.getState()),
    onCycleUnmovedUnit: () => api.getState().cycleUnmovedUnit(),
    onUndo,
    onEndTurn,
  });
}
